import {
  collection,
  doc,
  getDoc,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  COL,
  type Order,
  type OrderMessage,
  type OrderStatus,
  type Vendor,
  type VendorJob,
} from '@eprintsmart/shared';
import { db } from '../firebase';

const fns = () => getFunctions(undefined, 'asia-south1');

/* ------------------------------------------------------------------ *
 * Orders
 * ------------------------------------------------------------------ */

/** Statuses that still need someone to do something. */
export const OPEN_STATUSES: OrderStatus[] = [
  'PAID',
  'ARTWORK_REVIEW',
  'ARTWORK_REJECTED',
  'ASSIGNED',
  'VENDOR_ACCEPTED',
  'IN_PRODUCTION',
  'READY_TO_DISPATCH',
  'SHIPPED',
];

export function subscribeToOrders(
  onChange: (orders: Order[]) => void,
  options: { status?: OrderStatus; limit?: number } = {},
) {
  const constraints = [
    ...(options.status ? [where('status', '==', options.status)] : []),
    orderBy('createdAt', 'desc'),
    fsLimit(options.limit ?? 200),
  ];

  return onSnapshot(query(collection(db, COL.orders), ...constraints), (snap) => {
    onChange(snap.docs.map((d) => ({ ...(d.data() as Order), id: d.id })));
  });
}

export async function loadOrder(orderId: string): Promise<Order | null> {
  const snap = await getDoc(doc(db, COL.orders, orderId));
  return snap.exists() ? { ...(snap.data() as Order), id: snap.id } : null;
}

export async function updateOrderStatus(orderId: string, to: OrderStatus, note?: string) {
  return httpsCallable(fns(), 'updateOrderStatus')({ orderId, to, note });
}

export async function overrideArtworkWarning(orderId: string, orderItemId: string, reason: string) {
  return httpsCallable(fns(), 'overrideArtworkWarning')({ orderId, orderItemId, reason });
}

/**
 * The result matters. The server distinguishes a refund that was PAID from one
 * that is QUEUED because the Razorpay balance could not cover it, and this
 * used to throw that away — so an admin pressing "Issue refund" got the same
 * silence either way and no idea which had happened.
 */
export interface RefundResult {
  ok: boolean;
  queued?: boolean;
  refundId?: string;
  amountMinor: number;
  fullyRefunded?: boolean;
  remainingMinor?: number;
}

export async function refundOrder(payload: {
  orderId: string;
  amountMinor?: number;
  reason: string;
}): Promise<RefundResult> {
  const call = httpsCallable<typeof payload, RefundResult>(fns(), 'refundOrder');
  return (await call(payload)).data;
}

export async function assignVendor(payload: {
  orderId: string;
  assignments: { orderItemId: string; vendorId: string; vendorCostMinor?: number }[];
  note?: string;
}) {
  return httpsCallable(fns(), 'assignVendor')(payload);
}

export interface ArtworkFileView {
  id: string;
  orderItemId: string;
  originalFilename: string;
  status: string;
  checks: { code: string; severity: string; message: string }[];
  previewUrl?: string;
  widthMm: number | null;
  heightMm: number | null;
  effectiveDpi: number | null;
  /** AI enhancement: see functions/src/artwork/enhanceArtwork.ts. */
  enhancedFrom?: string;
  pendingChoice?: boolean;
  discarded?: boolean;
}

export async function loadArtwork(orderId: string): Promise<ArtworkFileView[]> {
  const { data } = await httpsCallable<{ orderId: string }, { files: ArtworkFileView[] }>(
    fns(),
    'getArtworkForOrder',
  )({ orderId });
  return data.files;
}

/**
 * Starts a download of the customer's original file. The link is signed for
 * half an hour and served as an attachment, so pointing the window at it
 * saves the file without leaving the page.
 */
export async function downloadArtworkOriginal(orderId: string, artworkFileId: string) {
  const { data } = await httpsCallable<
    { orderId: string; artworkFileId: string },
    { url: string; filename: string }
  >(fns(), 'getArtworkDownloadUrl')({ orderId, artworkFileId });
  window.location.assign(data.url);
}

/** The GST invoice raised when the order was paid, as a PDF download. */
export async function downloadInvoicePdf(orderId: string) {
  const { data } = await httpsCallable<
    { orderId: string },
    { url: string; invoiceNumber: string }
  >(fns(), 'getInvoiceUrl')({ orderId });
  window.location.assign(data.url);
}

/* ------------------------------------------------------------------ *
 * Vendors
 * ------------------------------------------------------------------ */

export function subscribeToVendors(onChange: (vendors: Vendor[]) => void) {
  return onSnapshot(query(collection(db, COL.vendors), orderBy('name')), (snap) => {
    onChange(snap.docs.map((d) => ({ ...(d.data() as Vendor), id: d.id })));
  });
}

export async function saveVendor(id: string, patch: Partial<Vendor>) {
  await setDoc(doc(db, COL.vendors, id), { ...patch, id, updatedAt: Date.now() }, { merge: true });
}

/**
 * Who produces the work when nobody has said otherwise.
 *
 * Read by `routeToDefaultVendor` the moment an order becomes reviewable. With
 * this unset an order simply waits for someone to assign it by hand — which is
 * how the first paid order came to sit untouched for days — so the Vendors page
 * shows what it currently is rather than leaving it invisible in Firestore.
 */
export function subscribeToDefaultVendor(onChange: (vendorId: string | null) => void) {
  return onSnapshot(doc(db, COL.settings, 'fulfilment'), (snap) => {
    onChange((snap.data() as { defaultVendorId?: string } | undefined)?.defaultVendorId ?? null);
  });
}

export async function setDefaultVendor(vendorId: string) {
  await setDoc(doc(db, COL.settings, 'fulfilment'), { defaultVendorId: vendorId, updatedAt: Date.now() }, { merge: true });
}

/**
 * Open jobs per vendor, for the load-balancing term in the ranking. Counted
 * from vendorJobs rather than orders because a vendor's workload is the jobs
 * they hold, not the orders those jobs belong to.
 */
export function subscribeToOpenJobCounts(onChange: (counts: Record<string, number>) => void) {
  return onSnapshot(
    query(
      collection(db, COL.vendorJobs),
      where('status', 'in', ['ASSIGNED', 'ACCEPTED', 'IN_PRODUCTION']),
    ),
    (snap) => {
      const counts: Record<string, number> = {};
      for (const d of snap.docs) {
        const job = d.data() as VendorJob;
        counts[job.vendorId] = (counts[job.vendorId] ?? 0) + 1;
      }
      onChange(counts);
    },
  );
}


/* ------------------------------------------------------------ settlements */

/**
 * What a vendor is owed for one month, and the acts of freezing and paying it.
 *
 * The statement is COMPUTED from dispatched jobs rather than accrued, exactly
 * as the designer one is: a derived figure can be recomputed and checked line
 * by line, where a running balance can only be trusted.
 */
export interface VendorSettlementView {
  settlement: {
    id: string;
    vendorId: string;
    period: string;
    jobCount: number;
    grossMinor: number;
    deductionsMinor: number;
    netPayableMinor: number;
    status: 'DRAFT' | 'APPROVED' | 'PAID';
    note?: string;
    paidAt?: number;
  } | null;
  grossMinor: number;
  lines: {
    jobId: string;
    orderNumber: string;
    productName: string;
    qty: number;
    amountMinor: number;
    dispatchedAt: number | null;
  }[];
}

export async function getVendorSettlement(payload: {
  vendorId: string;
  period: string;
}): Promise<VendorSettlementView> {
  const call = httpsCallable<typeof payload, VendorSettlementView>(fns(), 'getVendorSettlement');
  return (await call(payload)).data;
}

export async function approveSettlement(payload: {
  vendorId: string;
  period: string;
  deductionsMinor: number;
  note?: string;
}): Promise<void> {
  await httpsCallable(fns(), 'approveSettlement')(payload);
}

export async function markSettlementPaid(payload: {
  vendorId: string;
  period: string;
}): Promise<void> {
  await httpsCallable(fns(), 'markSettlementPaid')(payload);
}

/* ------------------------------------------------------------------ *
 * The conversation on an order
 * ------------------------------------------------------------------ */

/**
 * Live, because admin is signed in and the Firestore rule can key on that.
 *
 * The customer side cannot do this — guest checkout means there is usually no
 * `request.auth` for a rule to match, so they read the same collection through
 * the `getOrderMessages` callable. Two read paths, each matching what its own
 * side can actually prove. See functions/src/orders/messages.ts.
 */
export function watchOrderMessages(
  orderId: string,
  onChange: (messages: OrderMessage[]) => void,
): () => void {
  return onSnapshot(
    query(collection(db, COL.orders, orderId, 'messages'), orderBy('at', 'asc')),
    (snap) => onChange(snap.docs.map((d) => d.data() as OrderMessage)),
  );
}

/** Writes always go through the function: `from` is stamped from the caller's
 *  own claim, never accepted from this client. */
export async function postOrderMessage(payload: {
  orderId: string;
  body: string;
  attachments?: OrderMessage['attachments'];
}): Promise<{ messageId: string }> {
  const call = httpsCallable<typeof payload, { messageId: string }>(fns(), 'postOrderMessage');
  return (await call(payload)).data;
}

/** A 15-minute signed URL. Re-issued per request: a permanent link forwarded
 *  out of an inbox is a permanent leak. */
export async function orderMessageFileUrl(orderId: string, storagePath: string): Promise<string> {
  const call = httpsCallable<{ orderId: string; storagePath: string }, { url: string }>(
    fns(), 'getOrderMessageFileUrl',
  );
  return (await call({ orderId, storagePath })).data.url;
}

export async function orderMessageUploadUrl(payload: {
  orderId: string; filename: string; contentType: string;
}): Promise<{ uploadUrl: string; storagePath: string }> {
  const call = httpsCallable<typeof payload, { uploadUrl: string; storagePath: string }>(
    fns(), 'createOrderMessageUploadUrl',
  );
  return (await call(payload)).data;
}
